import './trim.toolbar.scss';
import { useCallback, useEffect, useRef, useState } from 'react';
import { RxRulerSquare } from 'react-icons/rx';
import { UiManager } from '../../../../../layout-editor/manager/ui/ui.manager';
import { ToolbarItemType, TRIM_TOOLBAR_ITEM_LIST } from '../../../../../models/sift/trim-toolbar-item-model';
import { Button, ButtonRectType } from '../../../../ui/button/button';
import { SwitchIcon } from '../../../../ui/switch-icon/switch-icon';
import iconHand from '../../../../../assets/img/icon/hand.svg';
import { TrimUiManager } from '../../../../../trim-editor/manager/ui/trim.ui.manager';
import { TrimFreeText } from './trim.free-text';
import { Pos } from '../../../../../models/pos';
import { MenuType } from '../../../layout/layout';
import { TextInfo } from '../../../layout/toolbar/layout.free-text';
import { dialogActions } from '../../../../dialog/slice/dialog-slice';
import { layoutActions } from '../../../../../slices/layout-slice';
import { EditableImage } from '../../../../../layout-editor/manager/image-edit/editable-image';
import { useDispatch } from 'react-redux';
import { TrimEditableImage } from '../../../../../trim-editor/manager/image-edit/trim.editable-image';
import { useParams } from 'react-router-dom';
import { PathParams } from '../../../../../routes/routing-path';
import { PrintTrimMenuType } from '../hooks/use-print-trim';
import { TrimKeyboardManager } from '../../../../../trim-editor/manager/keyboard/trim.keyboard.manager';

type TrimToolButtonProps = {
  // アイコン
  icon: string,
  // ラベル
  label: string,
  // クリックハンドラ
  onClick: () => void,
  // 簡易表示フラグ
  isSimple: boolean,
  // 選択状態
  isSelected: boolean,
  // 矩形情報の取得コールバック
  getRect?: (rect: ButtonRectType) => void,
  divider?: boolean,
  disabled?: boolean,
}
type TrimToolBarProps = {
  textPos: Pos | null,
  menuParam: PrintTrimMenuType;
  isRotateTemp: boolean
}

const TrimToolButton = (props: TrimToolButtonProps) => {
  const { icon, label, onClick, isSimple, getRect, isSelected, divider, disabled } = props;
  return (
    <>
      {divider ? <div className="horizon_divider" /> : (<></>)}
      <Button
        icon={icon === 'grid-ruler' ? <RxRulerSquare size={'1.8em'}/> : <img src={icon} />}
        label={isSimple ? undefined : label}
        onClick={onClick}
        color="dark"
        className={`tool_button${isSimple ? ' simple' : ''}${isSelected ? ' selected' : ''}`}
        size="sm"
        getRect={getRect}
        disabled={disabled}
      />
    </>
  );
};

const TrimToolbar = (props: TrimToolBarProps) => {
  const { textPos, menuParam, isRotateTemp } = props;
  const { kijshopCd, shopOrderId, orderId, imageId } = useParams<PathParams>();
  const dispatch = useDispatch();
  // - state -
  // -- 簡易表示 --
  const [isSimple, setIsSimple] = useState(false);
  // - ref -
  // -- ツールバー要素 --
  const toolbarEle = useRef<HTMLDivElement>(null);
  // -- ツールリスト --
  const [toolList, setToolList] = useState(TRIM_TOOLBAR_ITEM_LIST);
  // -- 選択状態 --
  const [selectTool, setSelectTool] = useState<ToolbarItemType>('select');
  // -- 画像選択モード --
  const [operationMode, setOperationMode] = useState<'pan-zoom' | 'select'>('select');
  const [isTrimMenu, setIsTrimMenu] = useState(false);
  const [isRuler, setIsRuler] = useState(true);
  const [isOpenTextTool, setIsOpenTextTool] = useState(false);
  // -- テキスト追加ボタンの矩形情報 --
  const [insTxtBtnRect, setInsTxtBtnRect] = useState<ButtonRectType>({});
  // -- ツールバーコンテンツのy軸オフセット値 --
  const [offsetTop, setOffsetTop] = useState(0);
  // -- ツールバーデータ --
  const [info, setInfo] = useState<TextInfo>({
    id: '',
    isBold: false,
    isItalic: false,
    textColor: '#808080',
    isOnDropper: false,
    fontType: '',
    font: '0',
    fontSize: '24',
    textTransparency: 100,
    dropShadow: false,
    transparency: 65,
    distance: 7,
    outlineWidth: 0,
    outlineColor: '#ff0000',
    textAlign: '0',
    textRotation: 0,
    textSelectRotation: 0,
    lineSpacingNum: 1,
    textAreaContents: '',
    height: 0,
  });
  // - callback -
  // -- 表示状態変更 --
  const handlerClickViewMode = useCallback((v) => {
    setIsSimple(v);
  }, []);

  const handlerChangeTool = useCallback((v: ToolbarItemType) => {
    if (v === 'redo' || v === 'undo' || v === 'ruler' || v === 'insert-stamp' || v === 'rotate-middle-frame' || v === 'reset') {
      // 「元に戻す」「やり直し」「ルーラー」「pngの配置」は変更なし状態
      // 「センター」「ルーラー」「画像一覧」のアクティブ状態はそれぞれ isCenter, isRuler, isViewList で管理
    } else if (selectTool === v) {
      setSelectTool('select');
    } else {
      setSelectTool(v);
    }
    switch (v) {
      case "select":
        const mode = selectTool === 'select'
          ? operationMode === 'pan-zoom' ? 'select' : 'pan-zoom'
          : operationMode;
        TrimUiManager.ins.emit('r->t:change-operation-mode', { mode });
        setOperationMode(mode);
        TrimUiManager.ins.emit('t->r:shortcut:change-operation-mode-reset', { isSelect: mode !== 'select' });
        break;
      case 'insert-stamp':
        onClickImageSelect();
        // UiManager.ins.emit('r->l:insert-image');
        break;
      case 'insert-text':
        setOperationMode('select');
        const _mode = selectTool === 'insert-text' ? 'select' : 'insert-text';
        setSelectTool(_mode);
        TrimUiManager.ins.emit('r->t:change-operation-mode', { mode: 'select' });
        if (_mode === 'insert-text') {
          TrimUiManager.ins.emit('r->t:insert-text:start', {
              callback: (isOpen: boolean, info: TextInfo) => {
                setInfo(info);
                TrimKeyboardManager.shortcutOff = isOpen;
                setIsOpenTextTool(isOpen);
              },
            });
        } else {
          TrimUiManager.ins.emit('r->t:insert-text:end', {});
          // UiManager.ins.emit('r->l:change-operation-cursor', { mode: 'select' });
        }
        break;
      case 'ruler':
        const _isRuler = !isRuler;
        setIsRuler(!isRuler);
        TrimUiManager.ins.emit(('r->t:ruler'), { display: _isRuler });
        break;
      case 'trimming':
        setIsTrimMenu(!isTrimMenu);
        setOperationMode('select');
        TrimUiManager.ins.emit('r->t:change-operation-mode', { mode: 'select' });
        if (isTrimMenu) {
          // ボタンで閉じる場合も設置途中の線の中断とフォーカス解除を通す
          TrimUiManager.ins.emit('r->t:close-trim-menu');
        }
        TrimUiManager.ins.emit('r->t:check:open-trim-menu', { isOpen: !isTrimMenu });
        break;
      case 'rotate-middle-frame':
        TrimUiManager.ins.emit('r->t:rotate-additional', {});
        break;
      case 'redo':
        TrimUiManager.ins.emit('r->t:redo');
        break;
      case 'undo':
        TrimUiManager.ins.emit('r->t:undo');
        break;
      case 'reset':
        dispatch(dialogActions.pushMessage({
          title: '確認',
          message: [
            'すべての画像・ページの編集作業をリセットします。',
            'よろしいですか？',
            '',
            '※作業を保存している場合は、最後に保存した状態に戻ります。'
          ],
          buttons: [
            { label: 'キャンセル', callback: () => dispatch(dialogActions.pop()) },
            { label: 'OK', callback: () => {
                dispatch(dialogActions.pop());
                window.location.reload();
              } },
          ]
        }))
        break;
      default:
        break;
    }
    console.log(`OnClick: ${v}`);
  }, [isRuler, isOpenTextTool, isTrimMenu, selectTool, operationMode]);

  const imagesize = (file: File) => {
    return new Promise<{ width: number, height: number }>((resolve, reject) => {
      const img = new Image();

      img.onload = () => {
        const size = {
          width: img.naturalWidth,
          height: img.naturalHeight,
        };

        URL.revokeObjectURL(img.src);
        resolve(size);
      };

      img.onerror = (error) => {
        reject(error);
      };

      img.src = URL.createObjectURL(file);
    });
  };

  const onClickImageSelect = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/png';
    input.multiple = false;
    input.onchange = async (e) => {
      if (input.files) {
        const size = await imagesize(input.files[0]);
        if ((size.width >= 500 && size.height >= 500) && (size.width <= 2000 && size.height <= 2000)) {
          TrimUiManager.ins.emit('t->r:wait-loading', { message: 'PNG画像を生成中です...' });
          TrimUiManager.ins.emit(
            'r->t:add-image:direct',
            {
              file: input.files[0],
              kijshopCd,
              shopOrderId,
              orderId: orderId || null,
              onCreatedEditableImage: (p) => {
                TrimUiManager.ins.emit('r->t:add-editable-image', {
                  editableImage: p.editableImage,
                });
              },
              callback: (p: { editableImage: TrimEditableImage }) => {
                // png 時は order-select は更新しない
                // dispatch(xmlActions.uploadImage({
                //   kijshopCd,
                //   shopOrderId,
                // }).layout(orderId || '').add(p.editableImage, () => {
                //   // if (String(p.editableImage.exif.colorSpace) !== '1') {
                //   //   dispatch(dialogActions.pushMessage({
                //   //     title: '確認',
                //   //     message: !p.editableImage.exif.colorSpace ? [
                //   //       'Exifヘッダ情報に色空間の情報がありません。',
                //   //       '色空間がsRGBでない場合、色が変わる可能性があります。',
                //   //     ] : [
                //   //       'sRGB以外の色空間が指定されています。',
                //   //     ],
                //   //     buttons: [{
                //   //       label: 'OK',
                //   //       callback: () => {
                //   //         dispatch(dialogActions.pop());
                //   //       },
                //   //     }],
                //   //   }));
                //   // }
                // }));
              },
            },
          );
        } else if (size.width < 500 || size.height < 500) {
          dispatch(
            dialogActions.pushMessage({
              title: '確認',
              message: [
                `PNG画像のサイズが小さすぎます(幅${size.width} x 高さ${size.height}ピクセル)。`,
                '幅500 x 高さ500ピクセル以上にしてください。',
              ],
              buttons: [
                {
                  label: 'OK',
                  callback: () => dispatch(dialogActions.pop()),
                },
              ],
            }),
          );
        } else {
          dispatch(
            dialogActions.pushMessage({
              title: '確認',
              message: [
                `PNG画像のサイズが大きすぎます(幅${size.width} x 高さ${size.height}ピクセル)。`,
                '幅2000 x 高さ2000ピクセル以内にしてください。',
              ],
              buttons: [
                {
                  label: 'OK',
                  callback: () => dispatch(dialogActions.pop()),
                },
              ],
            }),
          );
        }
      }
    };
    input.click();
  }, []);

  useEffect(() => {
    TrimUiManager.ins.on('t->r:show-text-tool', (v) => {
      setInfo(v.textInfo);
      TrimKeyboardManager.shortcutOff = v.isOpen;
      setIsOpenTextTool(v.isOpen);
    });
    TrimUiManager.ins.on('r->t:shortcut:change-operation-mode', (v) => {
      TrimUiManager.ins.emit('r->t:change-operation-mode', { mode: v.mode });
      setOperationMode(v.mode);
    });
    TrimUiManager.ins.on('r->t:shortcut:open-trim-menu', () => {
      handlerChangeTool('trimming');
    });
    TrimUiManager.ins.on('t->r:change-template', () => {
      setIsTrimMenu(false)
    });
    // パネルの実開閉イベントと同期する ( 閉じるボタン等での閉じを取りこぼさないため )
    const onOpenTrimMenu = (v: { isOpen: boolean }) => {
      setIsTrimMenu(v.isOpen);
      if (!v.isOpen) {
        setSelectTool('select');
      }
    };
    TrimUiManager.ins.on('t->r:open-trim-menu', onOpenTrimMenu);
    TrimUiManager.ins.on('t->r:push-dialog', (v) => {
      dispatch(dialogActions.pushMessage({
        title: v.title,
        message: v.message,
        buttons: v.buttons.map((v2) => ({
          ...v2,
          callback: () => {
            v2.callback();
            dispatch(dialogActions.pop());
          },
        })),
        remind: v.remind,
      }));
    });
    return () => {
      TrimUiManager.ins.off('t->r:show-text-tool');
      TrimUiManager.ins.off('r->t:shortcut:change-operation-mode');
      TrimUiManager.ins.off('r->t:shortcut:open-trim-menu');
      TrimUiManager.ins.off('t->r:change-template');
      TrimUiManager.ins.off('t->r:push-dialog');
      TrimUiManager.ins.off('t->r:open-trim-menu', onOpenTrimMenu);
    }
  }, []);

  // -- textModeの終了を検知 --
  useEffect(() => {
    if (selectTool !== 'insert-text' && !isOpenTextTool) {
      TrimUiManager.ins.emit('r->t:insert-text:end', ({}));
    }
  }, [selectTool, isOpenTextTool]);

  return (
    <div
      className="trim_toolbar"
      ref={toolbarEle}
      onClick={() => TrimUiManager.ins.emit('r->t:dis-select')}
    >
      <div className={`trim_toolbar__tools${isSimple ? ' simple' : ''}`}>
        <Button
          icon={
            <SwitchIcon
              isOn={isSimple}
              tmplMode="open_x"
            />}
          onClick={() => handlerClickViewMode(!isSimple)}
          color="dark"
          className="open_x_btn"
        />
        {toolList.map((v, i) => (
          <TrimToolButton
            key={`tool-button_${v.type}_${i}_${isSimple}`}
            icon={v.type === 'select' && operationMode === 'pan-zoom' ? iconHand : v.icon}
            label={v.label}
            onClick={() => handlerChangeTool(v.type)}
            isSimple={isSimple}
            isSelected={
              (!(v.type === 'select' || v.type === 'ruler') && selectTool === v.type)
              || (isRuler && v.type === 'ruler')
            }
            getRect={v.type === 'insert-text' ? setInsTxtBtnRect : undefined}
            divider={v.divider}
            disabled={v.type === 'rotate-middle-frame' && !isRotateTemp}
          />
        ))}
        {/*</TrimToolButton>*/}
        {/*{toolList.map((v, i) => (*/}
        {/*  <ToolButton*/}
        {/*    key={`tool-button_${v.type}_${i}_${isSimple}`}*/}
        {/*    icon={v.type === 'select' && operationMode === 'pan-zoom' ? iconHand : v.icon}*/}
        {/*    label={v.label}*/}
        {/*    onClick={() => handlerChangeTool(v.type)}*/}
        {/*    isSimple={isSimple}*/}
        {/*    isSelected={*/}
        {/*      (!(v.type === 'view-list' || v.type === 'select' || v.type === 'ruler' || v.type === 'center') && selectTool === v.type)*/}
        {/*      || (isViewList && v.type === 'view-list')*/}
        {/*      || (isRuler && v.type === 'ruler')*/}
        {/*      || (isCenter && v.type === 'center')*/}
        {/*    }*/}
        {/*    // isSelected={selectTool === 'view-list' && v.type === 'view-list'}*/}
        {/*    getRect={v.type === 'insert-text' ? setInsTxtBtnRect : undefined}*/}
        {/*    divider={v.divider}*/}
        {/*    disabled={(() => {*/}
        {/*      if (!album) {*/}
        {/*        if (v.type !== 'view-list') {*/}
        {/*          return true;*/}
        {/*        } else {*/}
        {/*          return false;*/}
        {/*        }*/}
        {/*      }*/}
        {/*      return v.type === disabledTool || disabledTool === 'both' ? v.type === 'redo' || v.type === 'undo' : false;*/}
        {/*    })()}*/}
        {/*  />*/}
        {/*))}*/}
      </div>

      {/* {selectTool === 'view-list' ? <LayoutImageList /> : (<></>)} */}
      {/*{selectTool === 'insert-text' ? (*/}
      {
        isOpenTextTool ? (
          <TrimFreeText
            info={info}
            // onGetTextInfo={(info) => {
            //   // console.log(info)
            // }}
            style={{
              top: textPos ? `calc(${info.adjustedPos?.y ?? textPos.y}px + ${info.height ? 0 : Number(info.fontSize) * (info.pos?.scale ?? 1)}px)` : offsetTop + (insTxtBtnRect.top || 0),
              left: textPos ? textPos.x : `calc(${insTxtBtnRect.width}px + 2em)`,
            }}
            popCallback={() => {
              setOperationMode('select');
              setSelectTool('select');
              TrimKeyboardManager.shortcutOff = false;
              setIsOpenTextTool(false);
            }}
            // onBlur={true}
          />
        ) : <></>
      }
    </div>
  );
};

export default TrimToolbar;
